import {useReactFlow, EdgeProps} from "@xyflow/react";
import {FlowNodeTypes} from "@/types/node";
import {generateId} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import {createNode} from "@/pages/flow/NodeTypes/FlowNode";
import {createFloatingEdge} from "@/pages/flow/EdgeTypes/FloatingEdge";
import useNode from "./useNode";
import {branchNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints/branch";

function useEdge(id: EdgeProps["id"]) {
  const {setEdges, setNodes, getNode, getEdge} = useReactFlow();
  const {getNodeParent} = useNode();

  const createPlaceholderNode = (position: {x: number; y: number}) => ({
    id: generateId(),
    position: {x: position.x, y: position.y},
    type: "placeholder",
    data: {type: "placeholder"},
  });

  const addEdges = (edgesToAdd: any[]) => {
    setEdges(edges => {
      const allEdges = edges.concat(edgesToAdd);
      return allEdges.map(e => ({...e, selected: false}));
    });
  };

  const addNodes = (nodesToAdd: any[]) => {
    setNodes(nodes => [...nodes, ...nodesToAdd]);
  };

  const addNodeOnEdge = (blueprint: any) => {
    const edge = getEdge(id);
    if (!edge) return;

    const targetNode = getNode(edge.target);
    const sourceNode = getNode(edge.source);
    if (!targetNode) return;

    const newNodeId = generateId();
    blueprint.id = newNodeId;
    const childNode = createNode(blueprint);

    const isTargetBranch = targetNode.type === FlowNodeTypes.BRANCH;
    const targetParentNode = isTargetBranch
      ? getNodeParent(targetNode.id)
      : null;

    const isSourceBranch = sourceNode?.type === FlowNodeTypes.BRANCH;
    const sourceParentNode = isSourceBranch
      ? getNodeParent(sourceNode.id)
      : null;

    const isBlueprintBranch = blueprint.type === FlowNodeTypes.BRANCH;
    const isBlueprintJump = blueprint.type === FlowNodeTypes.JUMP;
    const isBlueprintEnd = blueprint.type === FlowNodeTypes.END;

    const jumpToStepValue = isBlueprintJump
      ? (getSettingValueByKey(
          childNode.data.settings,
          "jump_to_step",
        ) as string)
      : null;

    // Parse jump_to_step value (format: "subflowId:nodeId" or just "nodeId" for backward compatibility)
    let jumpToNodeId = null;
    let isCrossSubflowJump = false;

    if (jumpToStepValue) {
      if (jumpToStepValue.includes(":")) {
        // Format is "subflowId:nodeId" - this is a cross-subflow jump
        isCrossSubflowJump = true;
        jumpToNodeId = jumpToStepValue.split(":")[1];
      } else {
        // Old format, just nodeId - same subflow
        jumpToNodeId = jumpToStepValue;
        isCrossSubflowJump = false;
      }
    }

    if ((isTargetBranch || isSourceBranch) && isBlueprintBranch) {
      const parentNode = sourceParentNode ?? targetParentNode;

      blueprint.position = {
        x: parentNode.position.x,
        y: parentNode.position.y,
      };

      const siblingNode = createNode(blueprint);
      const siblingPlaceholder = createPlaceholderNode(siblingNode.position);

      const siblingEdges = [
        {
          id: `${parentNode.id}->${newNodeId}`,
          source: parentNode.id,
          target: newNodeId,
          type: "workflow",
        },
        {
          id: `${newNodeId}->${siblingPlaceholder.id}`,
          source: newNodeId,
          target: siblingPlaceholder.id,
          type: "workflow",
        },
      ];

      addEdges(siblingEdges);
      addNodes([siblingNode, siblingPlaceholder]);
      return;
    }

    // Add child node
    blueprint.position = {
      x: targetNode.position.x,
      y: targetNode.position.y,
    };

    const isSpeakNode = blueprint.type === FlowNodeTypes.SPEAK;

    // For SPEAK nodes, create a branch node BEFORE it
    let branchNodeToInsert = null;
    let actualSourceNode = edge.source;

    if (
      isSpeakNode &&
      sourceNode?.type !== FlowNodeTypes.BRANCH &&
      sourceNode?.type !== FlowNodeTypes.ENTRY
    ) {
      // Create a branch node that will be inserted before the speak node
      const branchId = generateId();
      const branchBlueprint = {
        ...branchNodeBlueprint,
        id: branchId,
        position: {x: childNode.position.x, y: childNode.position.y},
      };
      branchNodeToInsert = createNode(branchBlueprint);

      // Update the source for the speak node to be the branch
      actualSourceNode = branchId;
    }

    const newEdges = [
      // If it's a speak node, add edge from original source to branch
      ...(isSpeakNode && branchNodeToInsert
        ? [
            {
              id: `${edge.source}->${branchNodeToInsert.id}`,
              source: edge.source,
              target: branchNodeToInsert.id,
              type: "workflow",
            },
          ]
        : []),
      // Add edge from source (or branch if speak) to the new node
      {
        id: `${actualSourceNode}->${newNodeId}`,
        source: actualSourceNode,
        target: newNodeId,
        type: "workflow",
      },
      // Add edge from new node to target
      {
        id: `${newNodeId}->${edge.target}`,
        source: newNodeId,
        target: edge.target,
        type: "workflow",
        ...(isBlueprintJump || isBlueprintEnd ? {hidden: true} : {}),
      },
      // Only add floating edge if it's a jump within the same subflow
      ...(isBlueprintJump && !isCrossSubflowJump && jumpToNodeId
        ? [
            createFloatingEdge({
              sourceNodeId: newNodeId,
              targetNodeId: jumpToNodeId,
            }),
          ]
        : []),
    ];

    setEdges(edges => {
      const filteredEdges = edges.filter(e => e.id !== id);
      const updatedEdges = isTargetBranch
        ? filteredEdges.map(e =>
            e.source === edge.source ? {...e, source: newNodeId} : e,
          )
        : filteredEdges;

      return updatedEdges.concat(newEdges);
    });

    setNodes(nodes => {
      const targetIndex = nodes.findIndex(node => node.id === edge.target);

      // If it's a speak node, insert both branch and speak nodes
      const nodesToInsert =
        isSpeakNode && branchNodeToInsert
          ? [branchNodeToInsert, childNode]
          : [childNode];

      return [
        ...nodes.slice(0, targetIndex),
        ...nodesToInsert,
        ...nodes.slice(targetIndex),
      ];
    });
  };

  return addNodeOnEdge;
}

export default useEdge;
